declare global {
  // Injected by jobiqo_job_widget.js at runtime
  const jobiqoJobWidget: { init(config: object): void } | undefined;
}

export interface JobiqoFilterConfig {
  companies: string;
  employmentTypes: string[];
  occupationalFields: string[];
  industries: string[];
  event: string;
}

/** Splits a comma-separated string into a trimmed, non-empty array. */
export function parseList(str: string): string[] {
  return str ? str.split(',').map((s) => s.trim()).filter(Boolean) : [];
}

interface TaxonomyCondition {
  name: string;
  value: string;
  operator: '=';
}

interface ExtraConditions {
  conjunction: 'OR';
  conditions: Array<{ operator: '='; name: string; value: string }>;
}

export interface JobiqoFilters {
  taxonomy?: TaxonomyCondition[];
  extraConditions?: ExtraConditions;
}

/**
 * Builds the taxonomy (AND) and extraConditions (OR) parts of the Jobiqo widget filters.
 *
 * Fields with a single value go to filters.taxonomy, which the widget ANDs together.
 * The one field that may contain multiple values — enforced by the CMS via
 * JobiqoFilterValidator (src/module/Validator/JobiqoFilterValidator.php) — goes to
 * filters.extraConditions with OR conjunction.
 *
 * Because at most one field can have multiple values (CMS invariant), the combined result
 * expresses: AND(single-value conditions, OR(multi-value conditions)).
 */
export function buildFilters(config: JobiqoFilterConfig): JobiqoFilters {
  const taxonomy: TaxonomyCondition[] = [];
  let extraConditions: ExtraConditions | undefined;

  function addFieldConditions(fieldName: string, values: string[]): void {
    if (values.length === 1) {
      taxonomy.push({ name: fieldName, value: values[0], operator: '=' });
    } else if (values.length > 1) {
      extraConditions = {
        conjunction: 'OR',
        conditions: values.map((value) => ({ operator: '=', name: fieldName, value })),
      };
    }
  }

  addFieldConditions('job_organization_name', parseList(config.companies));
  addFieldConditions('field_job_employment_type', config.employmentTypes);
  addFieldConditions('field_job_occupational_field_parents_all', config.occupationalFields);
  addFieldConditions('job_industry', config.industries);
  addFieldConditions('field_efellows_job_event', parseList(config.event));

  const result: JobiqoFilters = {};
  if (taxonomy.length > 0) result.taxonomy = taxonomy;
  if (extraConditions) result.extraConditions = extraConditions;
  return result;
}

/**
 * Strips ", Deutschland" from location text rendered by the Jobiqo widget.
 *
 * The widget renders location as a text node inside:
 *   [data-testid="job-teaser-wrapper"] .content .info > div:not(:first-child) > div
 * That div contains an icon <span> followed by a plain text node, e.g. "München, Deutschland".
 *
 * A persistent MutationObserver handles jobs loaded asynchronously (including tab switches).
 * Processed divs are marked with `data-country-stripped` to avoid double-processing.
 */
export function stripCountryInLocationStrings(widgetEl: HTMLElement): void {
  const LOCATION_INNER_DIV_SELECTOR =
    '[data-testid="job-teaser-wrapper"] .content .info > div:not(:first-child) > div';

  /**
   * Cleans a single location segment (one city entry, without the pipe separator).
   *
   * Jobiqo can return location strings in various forms:
   *   - "München, Deutschland"                                  → "München"
   *   - "85 München-Flughafen, Deutschland"                    → "München-Flughafen"
   *   - "Kreisfreie Stadt Berlin, Berlin, Deutschland"         → "Berlin"
   *   - "Kreisfreie Stadt Augsburg, 86 Augsburg, Deutschland"  → "Augsburg"
   *   - "Brüssel, Belgien"                                     → "Brüssel"
   *
   * Strategy: drop the last comma-separated segment (country) when present, then
   * take the remaining last segment (city, possibly with a leading postal code to strip).
   */
  function cleanLocationPart(part: string): string {
    const segments = part.trim().split(', ');
    // Drop the last segment (country) only when multiple segments are present
    if (segments.length > 1) segments.pop();
    // Remaining last segment is the city, possibly with a leading postal code
    const city = segments[segments.length - 1];
    // Remove leading postal code digits e.g. "86 Augsburg" → "Augsburg"
    const result = city.replace(/^\d+ /, '').trim();
    return result;
  }

  function processDiv(div: HTMLElement): void {
    if (div.dataset.countryStripped === '1') return;
    for (const node of div.childNodes) {
      if (node.nodeType === Node.TEXT_NODE && node.textContent) {
        node.textContent = node.textContent.split(' | ').map(cleanLocationPart).join(', ');
      }
    }
    // eslint-disable-next-line no-param-reassign
    div.dataset.countryStripped = '1';
  }

  function processAll(): void {
    const nodes = widgetEl.querySelectorAll<HTMLElement>(LOCATION_INNER_DIV_SELECTOR);
    nodes.forEach(processDiv);
  }

  const observer = new MutationObserver(processAll);
  observer.observe(widgetEl, { childList: true, subtree: true });
}

/** Maximum time to wait for the widget to load before checking anyway. */
const WIDGET_LOAD_TIMEOUT_MS = 5000;

/**
 * Sets data-few-slides on the slider widget element when fewer real slides than
 * slidesToShow are rendered.
 *
 * The widget's injected CSS makes the Slick track a flex container and sets
 * flex:1 on slides. When the track is sized for fewer slides than slidesToShow,
 * the flex:1 splits the track width among all elements (real + clones), making
 * tiles smaller than intended. Visually this causes a brief flash: cloned slides
 * are visible alongside real ones until the fix is applied.
 *
 * CSS uses data-few-slides to override: flex:none on slides (restoring slick's
 * calculated width), width:auto on the track, and hidden clones/arrows.
 *
 * Timing strategy: Slick inserts real slides and clones in the same DOM operation,
 * so the moment the first clone appears in the DOM, real slides are already there.
 * We watch for clone insertion and apply the fix immediately in that callback —
 * no settle delay needed, which eliminates the visible flash.
 */
export function fixSliderForFewSlides(widgetEl: HTMLElement, slidesToShow = 3): void {
  let alreadyFixed = false;

  const applyFix = (): void => {
    if (alreadyFixed) return;
    alreadyFixed = true;

    const realSlides = widgetEl.querySelectorAll('.slick-slide:not(.slick-cloned)');
    if (realSlides.length > 0 && realSlides.length < slidesToShow) {
      // eslint-disable-next-line no-param-reassign -- Intentional: setting a data attribute on the widget root element
      widgetEl.dataset.fewSlides = '1';
    }
  };

  // Slick may build the entire slider structure off-DOM before inserting it, so a clone
  // can appear either as a directly added node or nested inside a larger added subtree
  // (e.g. the whole .slick-slider). Both cases are covered below.
  const containsClone = (node: Node): boolean =>
    node instanceof Element &&
    (node.classList.contains('slick-cloned') || node.querySelector('.slick-cloned') !== null);

  const observer = new MutationObserver((mutations) => {
    const clonesInserted = mutations.some((m) => Array.from(m.addedNodes).some(containsClone));
    if (clonesInserted) {
      observer.disconnect();
      applyFix();
    }
  });

  observer.observe(widgetEl, { childList: true, subtree: true });

  // Safety net: if Slick never adds clones (widget fails to load, returns 0 jobs, etc.),
  // run applyFix after WIDGET_LOAD_TIMEOUT_MS. With 0 real slides the condition inside
  // applyFix is false, so data-few-slides is not set unnecessarily.
  setTimeout(() => {
    observer.disconnect();
    applyFix();
  }, WIDGET_LOAD_TIMEOUT_MS);
}

// ---------------------------------------------------------------------------
// Company logo alt text fix
// ---------------------------------------------------------------------------
// The Jobiqo widget incorrectly sets alt="Logo for job [job title]" on company
// logo images. The company name is available in the GraphQL response the widget
// fetches, but is not used when rendering the <img> element. The two functions
// below work together to fix this:
//   installLogoOrganizationInterceptor() — intercepts the fetch to capture names.
//   fixLogoAltText()                     — patches alt/title once imgs appear in DOM.
// ---------------------------------------------------------------------------

/**
 * Logo URL → company name map, shared across all Jobiqo widget instances on the page.
 * Populated by installLogoOrganizationInterceptor() as GraphQL responses arrive.
 */
const logoOrganizationMap = new Map<string, string>();
let logoOrganizationInterceptorInstalled = false;

/**
 * Installs a fetch interceptor that captures company names from Jobiqo GraphQL
 * responses and indexes them by logo URL, so fixLogoAltText() can use them later.
 *
 * Why an interceptor: the widget fetches job data — including the company name — via
 * GraphQL, but renders logo images with the job title in the alt attribute instead.
 * The company name never appears in the rendered DOM, so we must read it from the
 * network response before the widget processes it.
 *
 * How it works:
 *   1. Wraps window.fetch. All processing runs inside a single try/catch that wraps
 *      everything after the real fetch completes, so any unexpected error in our code
 *      is silently swallowed and the original response is always returned to the caller.
 *      If Jobiqo changes their API shape or URL, the worst outcome is that the map
 *      stays empty and logo alt texts are not updated — nothing else on the page breaks.
 *   2. Skips requests whose URL does not contain "/graphql" with minimal overhead.
 *   3. Clones and awaits the response body before returning it to the widget. This
 *      ensures the map is populated before the widget renders cards, so fixLogoAltText()
 *      always finds company names ready when images appear in the DOM.
 *   4. Stores two logo URL variants per job (small job-level and medium org-profile)
 *      because either may appear as img.src depending on how a job is configured.
 *
 * Safe to call from multiple modules — installs the interceptor only once (singleton).
 *
 * @returns The shared map, to be passed to fixLogoAltText().
 */
export function installLogoOrganizationInterceptor(): Map<string, string> {
  if (logoOrganizationInterceptorInstalled) return logoOrganizationMap;
  logoOrganizationInterceptorInstalled = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async function jobiqoLogoInterceptor(
    ...args: Parameters<typeof fetch>
  ): Promise<Response> {
    // Always call the real fetch first — its result must be returned unconditionally.
    const response = await originalFetch(...args);

    // All map-population logic is wrapped in a single broad try/catch.
    // If anything here throws (unexpected API shape, our own bug, anything else),
    // the catch silently swallows it and execution falls through to `return response`.
    // This guarantees the interceptor can only fail to update alt texts — it can
    // never break other fetch calls on the page.
    try {
      let url: string;
      if (args[0] instanceof Request) {
        url = args[0].url;
      } else if (args[0] instanceof URL) {
        url = args[0].href;
      } else {
        url = String(args[0]);
      }

      if (url.includes('/graphql')) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const data = await response.clone().json();
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        for (const page of (data?.data?.jobs?.pages ?? []) as Array<{
          organization?: string;
          logo?: { url?: string } | null;
          organizationProfile?: { logo?: { url?: string } | null } | null;
        }>) {
          if (page.organization) {
            // Job-level logo (style: job_squared_logo_small) — present when the job
            // has its own logo uploaded directly. May be null for some jobs.
            if (page.logo?.url) {
              const { pathname, search } = new URL(page.logo.url, window.location.origin);
              const logoRelativePath = pathname + search;
              logoOrganizationMap.set(logoRelativePath, page.organization);
            }

            // Organization-profile logo (style: job_squared_logo_medium) — used when
            // the job has no own logo but belongs to a company with a profile logo.
            if (page.organizationProfile?.logo?.url) {
              const { pathname, search } = new URL(page.organizationProfile.logo.url, window.location.origin);
              const logoRelativePath = pathname + search;
              logoOrganizationMap.set(logoRelativePath, page.organization);
            }
          }
        }
      }
    } catch {
      // Silently skip — map stays as-is, logo alt texts will simply not be updated.
    }

    return response;
  };

  return logoOrganizationMap;
}

/**
 * Fixes company logo alt text in the Jobiqo widget.
 *
 * The widget sets alt="Logo for job [job title]" on company logo images, using the
 * job title instead of the company name. This function watches the widget container
 * for new images and replaces both the alt and title attributes with
 * "[Company Name] Logo", using names from the map built by
 * installLogoOrganizationInterceptor().
 *
 * Handles Slick slider clones: Slick duplicates job cards for its infinite-loop
 * effect. Each clone carries the same img.src, so the same map lookup applies.
 * Processed images are marked with data-logo-alt-fixed to skip redundant calls.
 *
 * @param widgetEl - The #jobiqo_job_widget element to observe.
 * @param map      - The logo URL → company name map from installLogoOrganizationInterceptor().
 */
export function fixLogoAltText(widgetEl: HTMLElement, map: Map<string, string>): void {
  // Targets all unprocessed images inside the widget. The map lookup below acts as
  // the filter — only images whose src is known to belong to a company get updated.
  // A broad selector is intentional: it does not rely on the widget's current alt
  // text format, so it works even if Jobiqo changes how they word the alt attribute.
  const LOGO_IMG_SELECTOR = 'img:not([data-logo-alt-fixed])';

  function processImg(img: HTMLImageElement): void {
    if (img.dataset.logoAltFixed) return;

    const { pathname, search } = new URL(img.src);
    const imgRelativePath = pathname + search;
    const companyName = map.get(imgRelativePath);
    if (!companyName) return;

    const newAlt = `Logo von ${companyName}`;
    img.setAttribute('alt', newAlt);
    img.setAttribute('title', newAlt);
    // eslint-disable-next-line no-param-reassign
    img.dataset.logoAltFixed = '1';
  }

  function processAll(): void {
    widgetEl.querySelectorAll<HTMLImageElement>(LOGO_IMG_SELECTOR).forEach(processImg);
  }

  const observer = new MutationObserver(processAll);
  observer.observe(widgetEl, { childList: true, subtree: true });
}

// ---------------------------------------------------------------------------

/**
 * Hides the section if the Jobiqo widget renders no jobs.
 *
 * The widget fetches data first, then initializes Slick with the results in one DOM
 * operation. The '.slick-initialized' class only appears after the API response is
 * processed — it is the widget's own "done" signal, present whether jobs were found
 * or not. Watching for it avoids the false-hide on slow connections that a settle-timer
 * approach causes (a timer can fire while the widget is still waiting for the network).
 *
 * WIDGET_LOAD_TIMEOUT_MS acts as a safety net: if the widget JS fails to load entirely
 * (network error, script blocked, etc.), Slick never initializes and the observer never
 * fires — the timeout ensures the check still runs after 5 seconds.
 */
export function hideOnEmpty(widgetEl: HTMLElement, section: HTMLElement): void {
  let alreadyChecked = false;

  const onWidgetReady = (): void => {
    if (alreadyChecked) return;
    alreadyChecked = true;

    const hasJobs = widgetEl.querySelector('a') !== null;
    if (!hasJobs) {
      section.style.setProperty('display', 'none');
    }
  };

  // Slick may build the entire slider structure off-DOM before inserting it, so
  // '.slick-initialized' can appear either on a directly added node or nested inside
  // a larger added subtree (e.g. the whole .slick-slider). Both cases are covered below.
  const containsSlickInit = (node: Node): boolean =>
    node instanceof Element &&
    (node.classList.contains('slick-initialized') || node.querySelector('.slick-initialized') !== null);

  const observer = new MutationObserver((mutations) => {
    const slickReady = mutations.some((m) => Array.from(m.addedNodes).some(containsSlickInit));
    if (slickReady) {
      observer.disconnect();
      onWidgetReady();
    }
  });

  observer.observe(widgetEl, { childList: true, subtree: true });

  // Safety net: if the widget JS fails to load entirely, Slick never initializes and
  // the observer never fires. After WIDGET_LOAD_TIMEOUT_MS we run the check anyway —
  // an empty widget after 5s is safe to hide.
  setTimeout(() => {
    observer.disconnect();
    onWidgetReady();
  }, WIDGET_LOAD_TIMEOUT_MS);
}
